import {FormControl, FormField, Label, MultiChoiceProps} from 'platform/components';
import {VStack} from 'platform/foundation';

import {FieldPathByValue, FieldValues} from 'react-hook-form';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleSelectionOperator} from '../../../types/VehicleSelectionOperator';

const OPERATOR_LABEL_KEYS: Record<VehicleSelectionOperator, string> = {
  INCLUDED: 'entity.promoPhotos.vehicleSelection.operators.anyOf',
  EXCLUDED: 'entity.promoPhotos.vehicleSelection.operators.everythingExcept',
};

interface VehicleSelectionListFieldProps<
  TFieldValues extends FieldValues,
> extends RequiredTestIdProps {
  control: FormControl<TFieldValues>;
  // The include or the exclude list of the attribute, by the current operator.
  name: FieldPathByValue<TFieldValues, string[] | Nullish>;
  label: string;
  options: MultiChoiceProps<string | number>['options'];
  isLoading?: boolean;
  isDisabled?: boolean;
  tooltip?: string;
  // null hides the operator link: the attribute has no "everything except" list or the form does
  // not offer it.
  operator: VehicleSelectionOperator | null;
  onOperatorChange: (operator: VehicleSelectionOperator) => void;
  onChange?: () => void;
}

// One list attribute of the vehicle selection. With an operator, the label carries a link that
// flips between "any of" and "everything except" and the field is bound to the matching list.
export function VehicleSelectionListField<TFieldValues extends FieldValues>(
  props: VehicleSelectionListFieldProps<TFieldValues>
) {
  const hasOperator = !isNil(props.operator);

  const field = (
    <FormField
      // The name follows the operator; a fresh field avoids carrying the old registration over.
      key={props.name}
      control={props.control}
      type="multiChoice"
      name={props.name}
      label={hasOperator ? undefined : props.label}
      aria-label={props.label}
      options={props.options}
      isLoading={props.isLoading}
      isDisabled={props.isDisabled}
      tooltip={hasOperator ? undefined : props.tooltip}
      placeholder={i18n.t('general.labels.selectOption')}
      menuInPortal
      onChange={props.onChange}
      data-testid={props['data-testid']}
    />
  );

  if (isNil(props.operator)) {
    return field;
  }

  const nextOperator =
    props.operator === VehicleSelectionOperator.INCLUDED
      ? VehicleSelectionOperator.EXCLUDED
      : VehicleSelectionOperator.INCLUDED;

  return (
    <VStack data-testid={suffixTestId('withOperator', props)}>
      <Label
        tooltip={props.tooltip}
        actions={[
          {
            title: i18n.t(OPERATOR_LABEL_KEYS[props.operator]),
            leftIcon: 'action/swap_horiz',
            isDisabled: props.isDisabled,
            onClick: () => props.onOperatorChange(nextOperator),
          },
        ]}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Label>
      {field}
    </VStack>
  );
}
